import { toBigWithDefault, type Security } from '@talos/kyoko';
import type { PriceGridFilterState } from './types';

export const PriceGridLadderType = {
  Ladder: 'ladder',
  Book: 'book',
} as const;
export type PriceGridLadderType = (typeof PriceGridLadderType)[keyof typeof PriceGridLadderType];

export const DEFAULT_PRICE_GRID_PRICE_INCREMENT = '0.01';
export const DEFAULT_PRICE_LADDER_DEPTH = 5;
export const MAX_PRICE_GRID_PRICE_INCREMENT = '1000';

const LADDER_TYPE_KEY = 'ladderType';
const PRICE_INCREMENT_KEY = 'priceIncrement';
const LEGACY_PRICE_LADDER_TYPE = 'price';

export function getLadderType(filterState: PriceGridFilterState): PriceGridLadderType {
  const ladderType = filterState.custom?.[LADDER_TYPE_KEY];
  return ladderType === PriceGridLadderType.Book || ladderType === LEGACY_PRICE_LADDER_TYPE
    ? PriceGridLadderType.Book
    : PriceGridLadderType.Ladder;
}

export function setLadderType(
  filterState: PriceGridFilterState,
  ladderType: PriceGridLadderType
): PriceGridFilterState {
  return {
    ...filterState,
    custom: {
      ...filterState.custom,
      [LADDER_TYPE_KEY]: ladderType === PriceGridLadderType.Ladder ? undefined : ladderType,
    },
  };
}

export function getPriceIncrement(filterState: PriceGridFilterState): string {
  const increment = filterState.custom?.[PRICE_INCREMENT_KEY];
  return typeof increment === 'string' && increment.length > 0 ? increment : DEFAULT_PRICE_GRID_PRICE_INCREMENT;
}

export function setPriceIncrement(filterState: PriceGridFilterState, increment: string): PriceGridFilterState {
  return {
    ...filterState,
    custom: {
      ...filterState.custom,
      [PRICE_INCREMENT_KEY]: increment === DEFAULT_PRICE_GRID_PRICE_INCREMENT ? undefined : increment,
    },
  };
}

export function getMinPriceIncrement(securities: Security[]): string {
  if (securities.length === 0) {
    return DEFAULT_PRICE_GRID_PRICE_INCREMENT;
  }

  const minIncrement = securities.reduce<string | undefined>((min, security) => {
    const increment = security.MinPriceIncrement || DEFAULT_PRICE_GRID_PRICE_INCREMENT;
    const incrementBig = toBigWithDefault(increment, 0);
    if (incrementBig.eq(0)) {
      return min;
    }
    return min == null || incrementBig.lt(toBigWithDefault(min, 0)) ? increment : min;
  }, undefined);

  return minIncrement ?? DEFAULT_PRICE_GRID_PRICE_INCREMENT;
}

export function clampPriceIncrement(increment: string, minIncrement: string): string {
  const incrementBig = toBigWithDefault(increment, minIncrement);
  const minIncrementBig = toBigWithDefault(minIncrement, DEFAULT_PRICE_GRID_PRICE_INCREMENT);
  if (incrementBig.lte(minIncrementBig)) {
    return minIncrement;
  }
  if (incrementBig.gt(toBigWithDefault(MAX_PRICE_GRID_PRICE_INCREMENT, DEFAULT_PRICE_GRID_PRICE_INCREMENT))) {
    return MAX_PRICE_GRID_PRICE_INCREMENT;
  }
  return increment;
}

export function isPriceLadderMode(ladderType: PriceGridLadderType) {
  return ladderType === PriceGridLadderType.Book;
}
